import type { ComponentProps } from "react";
import type { LucideIcon } from "lucide-react";

type IconButtonProps = {
  icon: LucideIcon;
  /** Accessible name; also the default tooltip. */
  label: string;
  /** Tooltip override, e.g. why the action is disabled. */
  title?: string;
  tone?: "default" | "danger";
  /** md is the 44px touch target for standalone use; sm is 32px for dense grid rows. */
  size?: "md" | "sm";
  children?: never;
} & Omit<ComponentProps<"button">, "children" | "aria-label" | "title">;

const SIZES = { md: "min-h-[44px] min-w-[44px]", sm: "min-h-[32px] min-w-[32px]" } as const;

/** Icon button with aria-label and tooltip for grid row actions. */
export function IconButton({ icon: Icon, label, title, tone = "default", size = "md", className, type = "button", ...rest }: IconButtonProps) {
  const color = tone === "danger" ? "text-gray-500 hover:text-danger hover:bg-danger-bg" : "text-gray-500 hover:text-brand-navy hover:bg-gray-100";

  return (
    <button type={type} aria-label={label} title={title ?? label} className={`inline-flex ${SIZES[size]} cursor-pointer items-center justify-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green-50 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent ${color} ${className ?? ""}`} {...rest}>
      <Icon size={18} aria-hidden="true" />
    </button>
  );
}
